import { useUploadPicture } from '@api/upload-picture';
import { useVerifyOrganization } from '@api/verify-organization';
import {
	Button,
	Select,
	Text,
	TextInput,
} from '@components/v2/ui';
import { LanguageSelect } from '@components/v2/ui/LanguageSelect/LanguageSelect';
import { EUROPEAN_COUNTRIES } from '@config/constants/european-countries';
import {
	REGEX_EMAIL,
	REGEX_PHONE_NUMBER,
} from '@config/constants/regex';
import { useCreateContact } from '@features/offsets/api/create-contact';
import { useCreateOrganization } from '@features/offsets/api/create-organization';
import {
	Drawer,
	DrawerProps,
	Flex,
	SimpleGrid,
	Stack,
} from '@mantine/core';
import {
	isNotEmpty,
	matches,
	useForm,
} from '@mantine/form';
import useStore from '@store/index';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PictureUpload } from '../PictureUpload/PictureUpload';
import classes from './DrawerCreateOrganization.module.css';

const LANGUAGES_OPTIONS = [
	{
		label: 'Français',
		value: 'fr',
		flagUrl: 'https://flagcdn.com/w80/fr.png',
	},
	{
		label: 'English',
		value: 'en',
		flagUrl: 'https://flagcdn.com/w80/gb.png',
	},
];

interface OrganizationFormValues {
	organizationName: string;
	country: string;
	place: string;
	legalIdentifier: string;
	firstName: string;
	lastName: string;
	email: string;
	language: string;
	phoneNumber: string;
	logo: string | any;
}

const DrawerCreateOrganization = (
	props: DrawerProps & {
		onSuccess?: (values: any) => void;
	}
) => {
	const { t } = useTranslation();
	/* ******************************** Store States ******************************* */
	const { setCreatedOrganization } = useStore();

	const [pictureData, setPictureData] =
		useState<null | FormData>(null);

	const uploadPictureMutation = useUploadPicture({
		mutationConfig: {
			onSuccess(data) {
				form.setFieldValue('logo', data.id);
				const values = form.getValues();
				handleStartContactCreation(values, data.id);
			},
		},
	});

	const verifyOrganizationMutation =
		useVerifyOrganization({
			mutationConfig: {
				onSuccess: () => {},
			},
		});

	const createOrganizationMutation =
		useCreateOrganization({
			mutationConfig: {
				onSuccess: (data) => {
					if (form.getValues().legalIdentifier !== '') {
						verifyOrganizationMutation.mutate({
							id: data.id,
							legalIdentifier:
								form.getValues().legalIdentifier,
						});
					}
					setCreatedOrganization(data.id.toString());
					form.reset();
					setPictureData(null);
					props.onClose();
				},
			},
		});

	const createContactMutation = useCreateContact({
		mutationConfig: {
			onSuccess: (contactData) => {
				const values = form.getValues();
				createOrganizationMutation.mutate({
					body: {
						name: values.organizationName,
						location: {
							countryCode: values.country,
							place: values.place,
						},
						legalIdentifier: values.legalIdentifier,
						owner: contactData.id,
						...(values.logo ? { logo: values.logo } : {}),
					},
				});
			},
		},
	});

	const form = useForm<OrganizationFormValues>({
		mode: 'uncontrolled',
		initialValues: {
			organizationName: '',
			country: '',
			place: '',
			legalIdentifier: '',
			firstName: '',
			lastName: '',
			email: '',
			language: 'fr',
			phoneNumber: '',
			logo: '',
		},
		validate: {
			organizationName: isNotEmpty(
				t('global.organizationRequired')
			),
			country: isNotEmpty(t('global.countryRequired')),
			firstName: isNotEmpty(
				t('global.firstNameRequired')
			),
			lastName: isNotEmpty(t('global.lastNameRequired')),
			email: matches(
				REGEX_EMAIL,
				t('form.error.invalidEmail')
			),
			phoneNumber: matches(
				REGEX_PHONE_NUMBER,
				t('form.error.invalidPhoneNumber')
			),
		},
	});

	const handleStartContactCreation = (
		values: OrganizationFormValues,
		logoId?: string
	) => {
		createContactMutation.mutate({
			body: {
				firstName: values.firstName,
				lastName: values.lastName,
				email: values.email,
				locale: { language: values.language },
				...(values.phoneNumber.trim() !== ''
					? { phoneNumber: values.phoneNumber }
					: {}),
			},
		});
	};

	const handleSubmit = (
		values: OrganizationFormValues
	) => {
		if (pictureData) {
			uploadPictureMutation.mutate(pictureData);
		} else {
			handleStartContactCreation(values);
		}
	};

	const handleOnClose = () => {
		form.reset();
		form.clearErrors();
		setPictureData(null);
		props.onClose();
	};

	const handleOnUploadProfile = (
		formData: FormData | string | null
	) => {
		if (typeof formData !== 'string') {
			setPictureData(formData);
			form.setFieldValue('logo', 'pending');
		} else {
			setPictureData(null);
			form.setFieldValue('logo', '');
		}
	};

	const isPending =
		createContactMutation.isPending ||
		createOrganizationMutation.isPending;

	return (
		<Drawer
			opened={props.opened}
			onClose={handleOnClose}
			title={t('drawerCreateOrganization.title')}
			position="left"
			size="668px"
			zIndex={99}
			classNames={{
				...classes,
				title: classes.customTitle,
			}}
		>
			<form onSubmit={form.onSubmit(handleSubmit)}>
				<Stack h={'84vh'} justify="space-between">
					<Stack gap="16px">
						<PictureUpload
							onUpload={handleOnUploadProfile}
							src={''}
						/>

						<TextInput
							label={t('global.organizationName')}
							placeholder={t('global.organizationName')}
							withAsterisk
							{...form.getInputProps('organizationName')}
						/>

						<SimpleGrid cols={2}>
							<Select
								searchable
								withAsterisk
								key={form.key('country')}
								label={t('global.country')}
								data={EUROPEAN_COUNTRIES.map(
									(country) => ({
										...country,
										label: t(country.label),
									})
								)}
								placeholder={t(
									'placeholders.selectCountry'
								)}
								{...form.getInputProps('country')}
							/>
							<TextInput
								label={t('global.place')}
								placeholder={t('global.place')}
								{...form.getInputProps('place')}
							/>
						</SimpleGrid>

						<TextInput
							label={t('global.legalIdentifier')}
							placeholder={'Exp : 885 319 871'}
							{...form.getInputProps('legalIdentifier')}
						/>

						<Text
							variant="subtitle1"
							c={'#1D3965'}
							mt="md"
						>
							{t('drawerCreateOrganization.addUser')}
						</Text>

						<SimpleGrid cols={2}>
							<TextInput
								label={t('global.firstName')}
								placeholder={t('global.firstName')}
								withAsterisk
								{...form.getInputProps('firstName')}
							/>
							<TextInput
								label={t('global.lastName')}
								placeholder={t('global.lastName')}
								withAsterisk
								{...form.getInputProps('lastName')}
							/>
						</SimpleGrid>

						<TextInput
							label="E-mail"
							placeholder="E-mail"
							withAsterisk
							{...form.getInputProps('email')}
						/>

						<SimpleGrid cols={2}>
							<LanguageSelect
								withAsterisk
								value={form.values.language}
								onChange={(val) =>
									form.setFieldValue(
										'language',
										val || 'fr'
									)
								}
								options={LANGUAGES_OPTIONS}
								fullWidth={false}
								label={t('global.language')}
							/>
							<TextInput
								label={t('global.phoneNumber')}
								placeholder={'ex : +33345435345'}
								{...form.getInputProps('phoneNumber')}
							/>
						</SimpleGrid>
					</Stack>

					<Flex
						justify="space-between"
						gap={'12px'}
						mt="xl"
					>
						<Button
							fullWidth
							variant="outline"
							onClick={handleOnClose}
							disabled={isPending}
							style={{
								borderRadius: '24px',
								height: '48px',
							}}
						>
							{t('global.cancel')}
						</Button>
						<Button
							type="submit"
							fullWidth
							variant="primary"
							loading={isPending}
							style={{
								borderRadius: '24px',
								height: '48px',
								backgroundColor: '#2B3D63',
							}}
						>
							{t(
								'drawerCreateOrganization.btnValidation'
							)}
						</Button>
					</Flex>
				</Stack>
			</form>
		</Drawer>
	);
};

export default DrawerCreateOrganization;
